import { motion, AnimatePresence, Variants } from "framer-motion";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { X, Send, User, Phone } from "lucide-react";

type ChatFormData = {
  name: string;
  phone: string;
  message: string;
  consent: boolean;
};

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [isNotificationReady, setIsNotificationReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("chatFormSubmitted") === "true";
    }
    return false;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isDismissed = localStorage.getItem("chatNotificationDismissed") === "true";

      const timer = setTimeout(() => {
        setShowNotification(!isDismissed);
        setIsNotificationReady(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChatFormData>({
    defaultValues: { consent: true },
  });

  const toggleChat = () => setIsOpen((prev) => !prev);

  const popContainer: Variants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1,
      },
    },
  };

  const popItem: Variants = {
    hidden: { opacity: 0, y: 12, scale: 0.96 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } },
  };

  const sendButtonVariants: Variants = {
    hidden: { opacity: 0, scale: 0.9, y: 8 },
    show: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.25,
        ease: "easeInOut",
        delay: 0.15,
      },
    },
  };

  const onSubmit = async (data: ChatFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const payload = {
        name: data.name.trim(),
        phone: data.phone.trim(),
        email: "",
        service: "Kontaktformulär",
        description: data.message.trim(),
      };

      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/api/question`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Något gick fel. Försök igen senare.");
      }

      if (typeof window !== "undefined") {
        localStorage.setItem("chatFormSubmitted", "true");
      }

      setHasSubmitted(true);
      reset({ consent: true, name: "", phone: "", message: "" });
    } catch (error: any) {
      console.error("Form submission error:", error);
      setSubmitError(error.message || "Något gick fel. Försök igen senare.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* Notification Bubble */}
      <AnimatePresence>
        {isNotificationReady && showNotification && !isOpen && !hasSubmitted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-20 right-1.5 mb-2 mr-1.5 w-64 bg-white text-gray-800 text-base rounded-xl shadow-lg p-3.5 border border-gray-200"
          >
            <div className="absolute -bottom-2 right-4 w-4 h-4 bg-white transform rotate-45 border-r border-b border-gray-200"></div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (typeof window !== "undefined") {
                  localStorage.setItem("chatNotificationDismissed", "true");
                }
                setShowNotification(false);
              }}
              className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Stäng notis"
            >
              <X className="h-4.5 w-4.5" />
            </button>
            <div className="flex items-center gap-3 pr-4">
              <img
                src="/renglans4.webp"
                alt="Renglans Logo"
                className="w-10 h-10 object-contain flex-shrink-0"
              />
              <p className="font-sans text-base leading-snug">Ställ en fråga så återkommer vi så snart som möjligt!</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-20 right-0 w-[300px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-100"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0d76d8] to-[#0b69c3] text-white px-4 py-3.5 flex items-center justify-between shadow-sm border-b border-white/15">
              <div className="flex items-center gap-3 leading-tight">
                <div className="relative">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <img
                      src="/renglans4.webp"
                      alt="Renglans Logo"
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                  <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
                </div>
                <p className="text-base font-semibold tracking-[0.01em]">Har du en fråga?</p>
              </div>
              <button
                onClick={toggleChat}
                className="text-white/85 hover:text-white transition"
                aria-label="Stäng"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4 bg-[#f6f9fb]">
              {hasSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, y: 12, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }}
                  className="flex items-start gap-3"
                >
                  <img src="/renglans4.webp" alt="Renglans" className="w-9 h-9 rounded-full border border-blue-100" />
                  <div className="bg-white border border-gray-200 rounded-2xl px-3.5 py-2.5 text-sm text-gray-700 shadow-sm">
                    Tack! Vi återkommer så fort vi är lediga!
                  </div>
                </motion.div>
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 12, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }}
                    className="flex items-start gap-3"
                  >
                    <img src="/renglans4.webp" alt="Renglans" className="w-9 h-9 rounded-full border border-blue-100" />
                    <div className="bg-white border border-gray-200 rounded-2xl px-3.5 py-2.5 text-sm text-gray-700 shadow-sm">
                      Det här går direkt till oss. Vi svarar så fort vi är lediga!
                    </div>
                  </motion.div>

                  <motion.form
                    initial={{ opacity: 0, y: 12, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }}
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-3 bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }}
                      className="space-y-1"
                    >
                      <label htmlFor="name" className="text-xs font-semibold text-gray-700">Namn</label>
                      <div className="relative">
                        <User className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          id="name"
                          {...register("name", {
                            required: "Namn är obligatoriskt",
                            minLength: { value: 2, message: "Namnet måste vara minst 2 tecken" },
                            maxLength: 60
                          })}
                          className={`w-full rounded-lg border px-10 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#0d76d8]/30 focus:border-[#0d76d8] ${
                            errors.name ? "border-red-400" : "border-gray-300"
                          }`}
                          placeholder="För- och efternamn"
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.name && <p className="text-[11px] text-red-500">{errors.name.message}</p>}
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }}
                      className="space-y-1"
                    >
                      <label htmlFor="phone" className="text-xs font-semibold text-gray-700">Telefon</label>
                      <div className="relative">
                        <Phone className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          id="phone"
                          type="tel"
                          {...register("phone", {
                            required: "Telefonnummer är obligatoriskt",
                            maxLength: 25,
                            pattern: { value: /^[0-9\s+()\-]{7,}$/, message: "Ange ett giltigt telefonnummer" },
                          })}
                          className={`w-full rounded-lg border px-10 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#0d76d8]/30 focus:border-[#0d76d8] ${
                            errors.phone ? "border-red-400" : "border-gray-300"
                          }`}
                          placeholder="+46 70-000 00 00"
                          disabled={isSubmitting}
                        />
                      </div>
                      {errors.phone && <p className="text-[11px] text-red-500">{errors.phone.message}</p>}
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }}
                      className="space-y-1"
                    >
                      <label htmlFor="message" className="text-xs font-semibold text-gray-700">Meddelande</label>
                      <textarea
                        id="message"
                        {...register("message", {
                          required: "Meddelande är obligatoriskt",
                          minLength: { value: 5, message: "Skriv minst 5 tecken" },
                          maxLength: 500
                        })}
                        rows={3}
                        className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[#0d76d8]/30 focus:border-[#0d76d8] ${
                          errors.message ? "border-red-400" : "border-gray-300"
                        }`}
                        placeholder="Berätta kort hur vi kan hjälpa dig"
                        disabled={isSubmitting}
                      />
                      {errors.message && <p className="text-[11px] text-red-500">{errors.message.message}</p>}
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 12, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.32, ease: "easeOut" } }}
                    >
                      <label className="flex items-start gap-2 text-[12px] text-gray-600 leading-tight">
                        <input
                          type="checkbox"
                          {...register("consent", { required: "Du måste godkänna villkoren" })}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#0d76d8] focus:ring-[#0d76d8] accent-[#0d76d8]"
                          disabled={isSubmitting}
                        />
                        <span>
                          Genom att skicka godkänner du att vi kontaktar dig via telefon.
                        </span>
                      </label>
                      {errors.consent && <p className="text-[11px] text-red-500">{errors.consent.message}</p>}
                    </motion.div>

                    {submitError && <p className="text-[12px] text-red-600">{submitError}</p>}

                    <motion.button
                      variants={sendButtonVariants}
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2 bg-[#0d76d8] hover:bg-[#0b69c3] text-white font-semibold text-sm py-2.5 rounded-lg shadow-sm transition disabled:opacity-70"
                    >
                      {isSubmitting ? "Skickar..." : "Skicka"}
                      <Send className="h-4 w-4" />
                    </motion.button>
                  </motion.form>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        onClick={toggleChat}
        className="bg-[#0d76d8] p-4 rounded-full shadow-lg cursor-pointer hover:shadow-xl transition-all hover:bg-[#0b69c3] focus:outline-none focus:ring-2 focus:ring-[#0d76d8] focus:ring-offset-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: 1,
          y: isOpen ? 0 : [0, -10, 0],
        }}
        transition={{
          y: isOpen
            ? { duration: 0.3 }
            : {
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse" as const,
                ease: "easeInOut",
                delay: 1,
              },
          opacity: { duration: 0.3 },
        }}
        aria-label={isOpen ? "Stäng" : "Fråga oss"}
      >
        {isOpen ? (
          <X className="h-9 w-9 text-white" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-9 w-9 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        )}
      </motion.button>
    </div>
  );
};

export default ChatBubble;
